import { useCallback, useEffect, useRef } from 'react'

/**
 * Manages per-"stream" AbortControllers so that a newer fetch supersedes an
 * older in-flight one. Each logical request stream is identified by a string
 * `key` (e.g. 'WPC Review', 'Dealer Review', 'kpis'). Calling `getSignal(key)`
 * aborts the previous request for that key and returns a fresh `AbortSignal`.
 *
 * This fixes out-of-order response races: switching dealers or clicking
 * pagination rapidly fires overlapping requests, and without cancellation the
 * last response to *resolve* wins rather than the last one *requested*. Tying a
 * controller to each stream means only the most recent request can update state.
 *
 * All outstanding requests are aborted on unmount.
 */
export const useAbortControllers = () => {
  const controllers = useRef({})

  const getSignal = useCallback((key) => {
    controllers.current[key]?.abort()
    const controller = new AbortController()
    controllers.current[key] = controller
    return controller.signal
  }, [])

  useEffect(
    () => () => {
      Object.values(controllers.current).forEach((controller) => controller.abort())
    },
    [],
  )

  return { getSignal }
}

export default useAbortControllers
